import { router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { type ReactElement, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { useOnline } from "@/hooks/use-online"
import { adminBrandAgencyAssignmentsPath } from "@/routes"

interface AttachAgencyDialogProps {
  brandId: number
  brandName: string
  currentAgency: { id: number; name: string } | null
  agenciesAvailable: { id: number; name: string }[]
  /** Trigger element (e.g. an `<Button>`) — required to open the dialog. */
  trigger: ReactElement
}

/**
 * AttachAgencyDialog — single-select agency picker with replacement-aware
 * confirmation (UX Spec §3 F1 + AC5). Mounted from the Brand detail
 * Assignment card.
 *
 * Behaviour:
 *   - Search by agency name only (no admin_email exposure per UX Spec §8 Q4).
 *     `<Command>` does in-memory case-insensitive substring filtering on the
 *     loaded `agenciesAvailable` list — Backend's `serialise_agencies_available`
 *     already excludes the brand's current agency.
 *   - When `currentAgency !== null`, surface a `role="alert"` banner above
 *     the picker: "This will replace **{current agency}**". Primary button
 *     label flips Attach → Swap agency.
 *   - `Cancel` is autoFocused per UX Spec §5 — destructive-leaning surfaces
 *     never autofocus the destructive primary.
 *   - Submit goes through Inertia `<Form>`-equivalent (`router.post` with
 *     `agency_id` field) to `admin_brand_agency_assignments_path(brand_id)`.
 *     Backend renders a 303 redirect back to the Brand detail page; the
 *     `useFlash()` toast surfaces the result.
 *   - Disables when offline.
 *
 * Test IDs (locked v6 contract): `admin-agency-attach-dialog` (content),
 * `admin-agency-attach-submit` (primary button), `admin-agency-replace-warning`
 * (`role="alert"` banner — only present when `currentAgency` is not null).
 */
export function AttachAgencyDialog({
  brandId,
  brandName,
  currentAgency,
  agenciesAvailable,
  trigger,
}: AttachAgencyDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()

  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const isSwap = currentAgency !== null
  const noOptions = agenciesAvailable.length === 0

  // Reset local state on every open via a controlled wrapper around
  // `onOpenChange` — avoids the `set-state-in-effect` pattern that
  // ESLint flags. Resetting on close is also fine; the dialog is
  // unmounted-but-still-rendered between opens via the trigger.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setSelectedId(null)
      setSubmitting(false)
    }
    setOpen(next)
  }

  const submitDisabled = submitting || !online || selectedId === null

  function handleSubmit() {
    if (selectedId === null) return
    setSubmitting(true)
    router.post(
      adminBrandAgencyAssignmentsPath(brandId),
      { agency_id: selectedId },
      {
        preserveScroll: true,
        onFinish: () => {
          setSubmitting(false)
          setOpen(false)
        },
      },
    )
  }

  // Resolve the t() key OUTSIDE the ternary so each branch holds a
  // literal string. The `inertia_i18n_health_spec` scanner statically
  // matches `t("literal")` — ternary keys are invisible. This pattern
  // was set in OKS-11 (`roleLabel`) for the same reason.
  const dialogTitle = useMemo(
    () =>
      isSwap
        ? t("admin.brand_detail.attach_dialog.title_swap")
        : t("admin.brand_detail.attach_dialog.title_attach"),
    [t, isSwap],
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger render={trigger} />
      <DialogContent
        className="sm:max-w-md"
        data-testid="admin-agency-attach-dialog"
      >
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            {dialogTitle}
          </DialogDescription>
        </DialogHeader>

        {isSwap && (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm"
            data-testid="admin-agency-replace-warning"
          >
            <ReplaceWarning
              brandName={brandName}
              currentAgencyName={currentAgency.name}
            />
          </div>
        )}

        {noOptions ? (
          <div className="text-muted-foreground py-6 text-center text-sm">
            {t("admin.brand_detail.attach_dialog.no_options")}
          </div>
        ) : (
          <Command className="rounded-md border">
            <div className="flex items-center gap-2 border-b px-3">
              <Search
                className="text-muted-foreground size-4 shrink-0"
                aria-hidden="true"
              />
              <CommandInput
                autoFocus={!isSwap}
                placeholder={t(
                  "admin.brand_detail.attach_dialog.search_placeholder",
                )}
                aria-label={t("admin.brand_detail.attach_dialog.search_label")}
                className="border-0 outline-none focus:ring-0"
              />
            </div>
            <CommandList>
              <CommandEmpty>
                {t("admin.brand_detail.attach_dialog.empty")}
              </CommandEmpty>
              <CommandGroup>
                {agenciesAvailable.map((agency) => (
                  <CommandItem
                    key={agency.id}
                    value={agency.name}
                    onSelect={() => setSelectedId(agency.id)}
                    aria-label={t(
                      "admin.brand_detail.attach_dialog.select_label",
                      { name: agency.name },
                    )}
                    aria-selected={selectedId === agency.id}
                    data-active={selectedId === agency.id ? "true" : undefined}
                    data-testid="admin-agency-attach-option"
                    data-agency-id={agency.id}
                  >
                    <span>{agency.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            // UX Spec v2.1 §5: Attach modal autofocuses search (typeahead-
            // first); Destructive dialogs autofocus Cancel. The swap path
            // is destructive (replaces another admin's assignment) so
            // Cancel-autofocus applies; fresh attach autofocuses the
            // CommandInput above.
            autoFocus={isSwap}
            onClick={() => setOpen(false)}
          >
            {t("admin.brand_detail.attach_dialog.cancel")}
          </Button>
          <Button
            type="button"
            variant={isSwap ? "destructive" : "default"}
            onClick={handleSubmit}
            disabled={submitDisabled}
            aria-disabled={submitDisabled ? "true" : undefined}
            data-testid="admin-agency-attach-submit"
          >
            {isSwap
              ? t("admin.brand_detail.attach_dialog.submit_swap")
              : t("admin.brand_detail.attach_dialog.submit_attach")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Renders the replacement warning copy with bold formatting on the agency
 * name. i18next interpolation cannot emit bold tags safely, so we split
 * the string at the agency name and wrap the matched segment in `<strong>`.
 * Falls back to plain interpolation if the agency name doesn't appear
 * verbatim in the template (locale customisation safety net).
 */
function ReplaceWarning({
  brandName,
  currentAgencyName,
}: {
  brandName: string
  currentAgencyName: string
}) {
  const { t } = useTranslation()
  const raw = t("admin.brand_detail.attach_dialog.replace_warning", {
    current_agency: `__AGENCY__${currentAgencyName}__`,
    brand: brandName,
  })
  const parts = raw.split(/\*\*__AGENCY__|__\*\*/)
  // Expected shape: [prefix, agency_name, suffix]
  if (parts.length === 3) {
    return (
      <span>
        {parts[0]}
        <strong>{parts[1]}</strong>
        {parts[2]}
      </span>
    )
  }
  // Fallback if the locale string drops the markdown bold delimiters.
  return <span>{raw.replace(/\*\*/g, "")}</span>
}
